import { useEffect, useState } from "react";
import { Header } from "../components/Header";
import { AIInsightBanner } from "../components/AIInsightBanner";
import api from "../lib/api";
import { fmtINR, fmtPct, fmtNum } from "../lib/format";
import { Star, Shield, MapPin } from "lucide-react";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { ResponsiveContainer } from "../components/AutoSizer";

export default function MarketIntelligence() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/kpi/market-intelligence").then((r) => setData(r.data));
  }, []);

  if (!data) return <><Header title="Market Intelligence" /><div className="p-6">Loading...</div></>;

  const radarData = selected ? [
    { dim: "Price", v: selected.scores.price },
    { dim: "Quality", v: selected.scores.quality },
    { dim: "Location", v: selected.scores.location },
    { dim: "Brand", v: selected.scores.brand },
    { dim: "Delivery", v: selected.scores.delivery },
  ] : [];

  return (
    <>
      <Header title="Market Intelligence" subtitle="Competitive Landscape · Threat Assessment" />
      <div className="p-3 sm:p-6 md:p-8 max-w-[1600px] mx-auto space-y-5 min-w-0">
        <AIInsightBanner context={{ kpis: { sales_count: data.competitors.length } }} />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {data.cities.map((c) => (
            <div key={c.city} className="bg-white rounded-md border border-slate-200 p-4" data-testid={`city-card-${c.city}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin size={14} className="text-slate-500" />
                  <span className="font-semibold">{c.city}</span>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{c.competitor_count} players</span>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Avg ₹/sqft</div><div className="font-mono font-semibold">{fmtNum(c.avg_price_sqft)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Market Share</div><div className="font-mono font-semibold">{fmtPct(c.total_market_share)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Leader</div><div className="text-[11px] font-semibold truncate">{c.leader}</div></div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-md border border-slate-200 p-4">
            <div className="text-sm font-semibold mb-3">Market Share by City</div>
            <ResponsiveContainer width="100%" height={Math.max(260, data.competitors.length * 18)}>
              <BarChart data={data.competitors.slice(0, 15)} layout="vertical" margin={{ left: 100 }}>
                <CartesianGrid stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="competitor_name" width={120} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="market_share" fill="#D4AF37" name="Market Share %" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-md border border-slate-200 p-4">
            <div className="text-sm font-semibold mb-3">
              {selected ? `Profile · ${selected.competitor_name}` : "Tap a competitor to compare"}
            </div>
            {selected ? (
              <ResponsiveContainer width="100%" height={280}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#e2e8f0" />
                  <PolarAngleAxis dataKey="dim" tick={{ fontSize: 11, fill: "#0F1D32" }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 9 }} />
                  <Radar dataKey="v" stroke="#0F1D32" fill="#D4AF37" fillOpacity={0.4} />
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-xs text-slate-500 py-10 text-center">Select a competitor below to view multi-dimensional radar comparison.</div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-md border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold">Competitor Rankings & Threat Assessment</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="competitors-table">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-2">Competitor</th>
                  <th className="text-left px-4 py-2">City</th>
                  <th className="text-right px-4 py-2">Projects</th>
                  <th className="text-right px-4 py-2">₹/sqft</th>
                  <th className="text-right px-4 py-2">Share %</th>
                  <th className="text-right px-4 py-2">Rating</th>
                  <th className="text-left px-4 py-2">Threat</th>
                </tr>
              </thead>
              <tbody>
                {data.competitors.map((c) => {
                  const total = (c.scores.price + c.scores.quality + c.scores.location + c.scores.brand + c.scores.delivery) / 5;
                  const threat = total > 80 ? "High" : total > 65 ? "Medium" : "Low";
                  return (
                    <tr key={c.id} onClick={() => setSelected(c)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`competitor-row-${c.competitor_name.replace(/\s+/g, "-")}`}>
                      <td className="px-4 py-2 font-medium">{c.competitor_name}</td>
                      <td className="px-4 py-2">{c.city}</td>
                      <td className="numeric-cell px-4 py-2">{c.projects_count}</td>
                      <td className="numeric-cell px-4 py-2">{fmtNum(c.avg_price_sqft)}</td>
                      <td className="numeric-cell px-4 py-2">{c.market_share}</td>
                      <td className="numeric-cell px-4 py-2 inline-flex items-center justify-end gap-1"><Star size={11} className="text-amber-500" /> {c.rating}</td>
                      <td className="px-4 py-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono uppercase ${
                          threat === "High" ? "bg-red-50 text-red-700" :
                          threat === "Medium" ? "bg-amber-50 text-amber-700" :
                          "bg-emerald-50 text-emerald-700"
                        }`}>
                          <Shield size={9} className="inline mr-1" /> {threat}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {selected && (
          <div className="bg-white rounded-md border border-slate-200 p-5">
            <div className="text-sm font-semibold mb-3">Strategic Recommendation · {selected.competitor_name}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-mono mb-2">Their Strengths</div>
                <ul className="text-xs space-y-1">
                  {selected.strengths.map((s, i) => <li key={i} className="flex gap-2"><span className="text-emerald-600">▸</span>{s}</li>)}
                </ul>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-red-700 font-mono mb-2">Their Weaknesses (opportunity for us)</div>
                <ul className="text-xs space-y-1">
                  {selected.weaknesses.map((s, i) => <li key={i} className="flex gap-2"><span className="text-red-600">▸</span>{s}</li>)}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
